import { supabase } from './supabase';

// ============================================
// TYPES
// ============================================

export interface WeatherCurrent {
  temp: number;
  feels_like: number;
  humidity: number;
  description: string;
  icon: string;
  wind_speed: number;
}

export interface WeatherForecastDay {
  date: string;
  temp_high: number;
  temp_low: number;
  icon: string;
  description: string;
}

export interface WeatherData {
  current: WeatherCurrent;
  forecast: WeatherForecastDay[];
  location: string;
  cached: boolean;
  cached_at?: string;
}

export interface WeatherRequest {
  lat: number;
  lon: number;
  units?: 'imperial' | 'metric';
  forecastDays?: number;
}

// Weather icon mapping (OpenWeather codes to emoji)
export const WEATHER_ICONS: Record<string, string> = {
  '01d': '☀️', '01n': '🌙',
  '02d': '⛅', '02n': '☁️',
  '03d': '☁️', '03n': '☁️',
  '04d': '☁️', '04n': '☁️',
  '09d': '🌧️', '09n': '🌧️',
  '10d': '🌦️', '10n': '🌧️',
  '11d': '⛈️', '11n': '⛈️',
  '13d': '❄️', '13n': '❄️',
  '50d': '🌫️', '50n': '🌫️',
};

// ============================================
// GET WEATHER (via Edge Function)
// ============================================

export async function getWeather(request: WeatherRequest): Promise<WeatherData | null> {
  try {
    // Use direct fetch with anon key to allow unauthenticated (kiosk) access
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    const response = await fetch(`${supabaseUrl}/functions/v1/get-weather`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      console.error('Weather edge function error:', response.status, response.statusText);
      return null;
    }

    const data = await response.json();

    if (data?.error) {
      console.error('Weather API error:', data.error);
      return null;
    }

    return data as WeatherData;
  } catch (error) {
    console.error('Failed to fetch weather:', error);
    return null;
  }
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get user's current location via browser geolocation API
 */
export function getCurrentLocation(): Promise<{ lat: number; lon: number }> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
        });
      },
      (error) => {
        reject(error);
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes
      }
    );
  });
}

/**
 * Get default location from environment or hardcoded fallback
 */
export function getDefaultLocation(): { lat: number; lon: number } {
  return {
    lat: Number(import.meta.env.VITE_DEFAULT_LAT) || 42.3336,
    lon: Number(import.meta.env.VITE_DEFAULT_LON) || -88.2668,
  };
}

/**
 * Get weather icon emoji from OpenWeather icon code
 */
export function getWeatherIcon(iconCode: string): string {
  return WEATHER_ICONS[iconCode] || '🌤️';
}

/**
 * Format temperature with unit
 */
export function formatTemp(temp: number, units: 'imperial' | 'metric' = 'imperial'): string {
  return `${Math.round(temp)}°${units === 'imperial' ? 'F' : 'C'}`;
}

/**
 * Format wind speed with unit
 */
export function formatWindSpeed(speed: number, units: 'imperial' | 'metric' = 'imperial'): string {
  return `${Math.round(speed)} ${units === 'imperial' ? 'mph' : 'm/s'}`;
}
